import { useRef } from "react";
import { Dialog, DialogContent } from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { VersionModalHeader } from "./_VersionModalHeader";
import { useTranslation } from "react-i18next";

type TProps = {
  isOpen: boolean;
  value: string;
  isLoading: boolean;
  projectName?: string;
  projectLogoUrl?: string | null;
  projectIconShape?: string | null;
  projectIconColor?: string | null;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
};

export function RenameVersionModal({
  isOpen,
  value,
  isLoading,
  projectName,
  projectLogoUrl,
  projectIconShape,
  projectIconColor,
  onChange,
  onCancel,
  onSave
}: TProps) {
  const { t } = useTranslation("common");
  const fieldRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        data-testid="rename-version-modal"
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          fieldRef.current?.querySelector("input")?.focus();
        }}
        className="w-[480px] max-w-[480px] grid-cols-[minmax(0,1fr)] gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-300 ring-1 ring-inset ring-innoveraZinc-150 sm:rounded-xl"
      >
        <VersionModalHeader
          projectName={projectName}
          projectLogoUrl={projectLogoUrl}
          projectIconShape={projectIconShape}
          projectIconColor={projectIconColor}
          subtitle={t("versions.renameTitle")}
          onClose={onCancel}
        />

        <div ref={fieldRef} className="flex flex-col gap-2.5 px-5">
          <FigmaTypography component="span" variant="label-2" color="zinc900" className="flex h-[10px] items-center">
            {t("versions.nameLabel")}
          </FigmaTypography>
          <AppInput
            value={value}
            disabled={isLoading}
            data-testid="rename-version-name-input"
            className="h-9"
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (isEnterSubmit(e, { allowShift: true })) {
                e.preventDefault();
                onSave();
              }
            }}
          />
        </div>

        <div className="flex h-[72px] shrink-0 items-center justify-end gap-2 p-5">
          <AppButton
            type="button"
            appearance="outlined"
            color="zinc150"
            disabled={isLoading}
            onClick={onCancel}
            data-testid="rename-version-cancel-button"
          >
            {t("cancel")}
          </AppButton>
          <AppButton
            type="button"
            color="zinc950"
            isLoading={isLoading}
            onClick={onSave}
            data-testid="rename-version-save-button"
          >
            {t("versions.save")}
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
